import React, {useState} from 'react';
import {Badge, Icon, WithTooltip} from '@albert-io/atomic';
import {useIsFeatureFlagEnabled} from 'client/components/FeatureFlag/featureFlag.utils';
import sessionStore from 'client/Session/SessionStore';

import {useAdminContentLocks} from './useAdminContentLocks';
import {isEffectivelyLocked} from './contentLockDerivation';
import type {GuideTree} from './contentLockDerivation';
import {ManageRestrictionsModal} from './ManageRestrictionsModal';
import {GuideRestrictionMenu, QuestionRestrictionMenu} from './RestrictionMenu';

/** Feature flag gating the entire admin-managing-restrictions experience (indicators, action, modal). */
export const ADMIN_MANAGING_RESTRICTIONS_FLAG = 'admin_managing_restrictions';

// Per design (#17841) the admin indicator is a single solid lock; full and partial restriction
// both show the same lock (the per-school breakdown popover conveys the difference).
export const getLockVariant = (
  restrictedCount: number
): {icon: 'lock'; iconStyle: 'solid'} | null => {
  if (restrictedCount === 0) {
    return null;
  }
  return {icon: 'lock', iconStyle: 'solid'};
};

interface RestrictionIndicatorProps {
  guideLevelName: string;
  /** Lock source id. Drives the breakdown + whether the lock shows; absent → renders nothing. */
  guideLevelId?: string;
  /**
   * Where the indicator lives. Every non-`'guide'` variant is a "question variant" — same styling +
   * guide-link menu, differing only in where the caller renders the lock. `'guide'` opens the modal.
   */
  variant?: 'guide' | 'question' | 'student-submission' | 'question-metadata';
  /** (question variants) URL for the "Edit restriction settings in the assessment guide" CTA. */
  assessmentGuideHref?: string;
  placement?:
    | 'bottom-start'
    | 'bottom'
    | 'bottom-end'
    | 'top-start'
    | 'top'
    | 'right-start'
    | 'right'
    | 'left-start'
    | 'left';
  /**
   * Portal the tooltip content. Defaults to `false` (content stays a descendant so the edit action
   * stays clickable); set `true` inside a virtualized/transformed list where ancestor transforms
   * break inline fixed-positioning.
   */
  usePortal?: boolean;
  /**
   * (question variants) Extra guide-level ids checked alongside `guideLevelId` — a question's aligned
   * levels, so a lock on the level OR any ancestor shows it restricted. (Guide variant uses `guideLevelTree`.)
   */
  ancestorGuideLevelIds?: string[];
  /**
   * (guide variant) The guide's level tree, enabling full effective-lock derivation: locked if the
   * level, an ancestor, or all of its children are locked (downward + upward cascade).
   */
  guideLevelTree?: GuideTree;
  /**
   * Render as a "Restricted at N schools" badge instead of the bare lock icon. Opt-in — only the
   * subject-assessments guide-level list uses the badge; every other surface keeps the compact icon.
   */
  showAsBadge?: boolean;
}

/**
 * Lock shown next to a restricted assessment guide level or question (admin-locked-assessments,
 * Part 2). Clicking it opens a menu with the per-school restricted/unrestricted breakdown (and, for
 * the guide variant, an "Edit Settings" action). Click-to-open (not hover) so the menu stays put
 * while the pointer moves into it; it closes on an outside click or a second click on the lock.
 */
export const RestrictionIndicator = ({
  guideLevelName,
  guideLevelId,
  ancestorGuideLevelIds,
  guideLevelTree,
  variant = 'guide',
  assessmentGuideHref,
  placement = 'bottom-start',
  usePortal = false,
  showAsBadge = false
}: RestrictionIndicatorProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {isFeatureEnabled} = useIsFeatureFlagEnabled(ADMIN_MANAGING_RESTRICTIONS_FLAG);
  const enabled = sessionStore.isSchoolAdministrator() && isFeatureEnabled;

  const {schools, loading, getBreakdown, getBreakdownWhere} = useAdminContentLocks({enabled});
  // With the tree, derive full effective-lock state; otherwise a lock on the level or any aligned id counts.
  const {restricted, unrestricted} =
    guideLevelTree && guideLevelId
      ? getBreakdownWhere((lockedSourceIds) =>
          isEffectivelyLocked(guideLevelTree, guideLevelId, lockedSourceIds)
        )
      : getBreakdown(
          [guideLevelId, ...(ancestorGuideLevelIds ?? [])].filter((id): id is string => Boolean(id))
        );
  const lockVariant = getLockVariant(restricted.length);

  if (!enabled || loading || !lockVariant) {
    return null;
  }

  const isQuestionVariant = variant !== 'guide';
  const allRestricted = restricted.length === schools.length;
  const restrictedSchoolCount = restricted.length;
  const restrictionBadgeLabel = `Restricted at ${restrictedSchoolCount} ${
    restrictedSchoolCount === 1 ? 'school' : 'schools'
  }`;

  return (
    <>
      <WithTooltip
        behavior='click'
        theme='light'
        placement={placement}
        allowOnlyOneTooltip
        anchoredFixedPositionProps={{
          usePortal,
          // Anchor flip/overflow to the viewport so a side tooltip in a narrow list doesn't flip back over it.
          modifiers: {
            preventOverflow: {boundariesElement: 'viewport'},
            flip: {boundariesElement: 'viewport'}
          }
        }}
        content={
          isQuestionVariant ? (
            <QuestionRestrictionMenu
              restricted={restricted}
              unrestricted={unrestricted}
              allRestricted={allRestricted}
              assessmentGuideHref={assessmentGuideHref}
            />
          ) : (
            <GuideRestrictionMenu
              restricted={restricted}
              unrestricted={unrestricted}
              onEdit={() => setIsModalOpen(true)}
            />
          )
        }
      >
        {/* Trigger is a <button> for keyboard/click support. Default: the bare lock icon (aria-label
            supplies the name). Badge variant (subject-assessments list only): the Badge carries the
            accessible name via its visible text, so no aria-label (it would mismatch that text). */}
        <button
          type='button'
          aria-label={showAsBadge ? undefined : 'Restriction details'}
          aria-haspopup={isQuestionVariant ? 'dialog' : 'menu'}
          className={
            showAsBadge
              ? 'inline-flex cursor-pointer appearance-none border-0 bg-transparent p-x0'
              : isQuestionVariant
                ? 'inline-flex cursor-pointer appearance-none items-center justify-center rounded-x0-half border-0 bg-transparent p-x0-half text-xs transition-colors hover:bg-background-subtle'
                : 'inline-flex cursor-pointer appearance-none border-0 bg-transparent p-x0 text-m'
          }
        >
          {showAsBadge ? (
            <Badge
              status='neutral'
              icon={lockVariant.icon}
              iconStyle={lockVariant.iconStyle}
              className='!cursor-pointer'
            >
              {restrictionBadgeLabel}
            </Badge>
          ) : (
            <Icon
              icon={lockVariant.icon}
              iconStyle={lockVariant.iconStyle}
              color='secondary'
              size='inherit'
            />
          )}
        </button>
      </WithTooltip>
      {!isQuestionVariant && isModalOpen && (
        <ManageRestrictionsModal
          guideLevelName={guideLevelName}
          guideLevelId={guideLevelId}
          guideLevelTree={guideLevelTree}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </>
  );
};

export default RestrictionIndicator;
